import { AppState } from 'app-store'
import { locationActions } from '@common/state/location/actions'
import { flexActions } from '@common/state/flex/actions'
import { buyUserActions } from '@buy/state/user/actions'
import { buyFormActions } from '@buy/state/buy-form/actions'
import { buyCheckoutActions } from '@buy/state/checkout/actions'
import { getFatalError, getFlexState, getCheckoutState as getCommonCheckoutState } from '@common/state/selectors'
import { BuyPlan } from '@buy/state/plan'
import { BuyAbandonedCartFormData } from '@buy/state/user/state'
import { fatalErrorActions } from '@common/state/fatal-error/actions'
import { getLabelContent, getCheckoutState, getUserState, getBuyFormState, getPlanData } from '@buy/state/selectors'
import { BuyFormData } from '@buy/state/buy-form/state'
import { buyTrackingActions } from '@buy/state/tracking/actions'
import { TrackingState } from '@common/state/tracking/state'
import { BUY_TRACKING_COMPONENTS } from '@buy/constants'
import { FatalErrorMessageType } from '@common/state/fatal-error/state'
import { checkoutActions as commonCheckoutActions } from '@common/state/checkout/actions'
import { BuyCheckoutFormState } from './state'

export const mapBuyCheckoutFormState = (state: AppState): BuyCheckoutFormState => {
    const {
        signIn,
        changePaymentMethodModalHeader,
        changePaymentMethodModalSubheader,
        modalCloseButtonText,
        showMe,
        errorCardinalChallenge,
        errorCardinalSetup,
        existingUsersModalContent,
        existingUsersModalHeader,
        existingUsersNewEmailButtonText,
        changeCountryContent,
        changeCountryHeader,
        modalCancel,
        modalReload,
        loadingSpinnerText,
    } = getLabelContent(state)

    const { inlineErrors } = getCommonCheckoutState(state)

    const { billingTenure, baseLine, calculatedTotal } = getCheckoutState(state)
    const planData: BuyPlan = getPlanData(state)
    const { isCardValid, isCvvValid, userInteractedWithFlexField, keyInfo, isCardinalSetupComplete, fingerprint } =
        getFlexState(state)
    const {
        userType,
        existingPaymentInformation,
        loggedIn: { isUserLoggedIn },
    } = getUserState(state)
    const { clearEmail, statesOptional } = getBuyFormState(state)
    const { isFatalError, fatalErrorType } = getFatalError(state)
    const isScaEnabledForMid = keyInfo?.scaToken?.isScaEnabledForMid
    const tokenString = keyInfo?.scaToken?.tokenString

    return {
        content: {
            signIn,
            changePaymentMethodModalHeader,
            changePaymentMethodModalSubheader,
            modalCloseButtonText,
            showMe,
            errorCardinalChallenge,
            errorCardinalSetup,
            existingUsersModalContent,
            existingUsersModalHeader,
            existingUsersNewEmailButtonText,
            changeCountryContent,
            changeCountryHeader,
            modalCancel,
            modalReload,
        },
        clearEmail,
        userInteractedWithFlexField,
        selectedPlan: planData,
        isUserLoggedIn,
        isFatalError,
        fatalErrorType,
        userType,
        existingPaymentInformation,
        billingFrequency: billingTenure.selected,
        isCardValid,
        isCvvValid,
        inlineErrors,
        isScaEnabledForMid,
        tokenString,
        statesOptional,
        isCardinalSetupComplete,
        fingerprint,
        baseLine,
        loadingSpinnerText,
        totalLoaded: calculatedTotal > 0,
    }
}

export const mapBuyCheckoutFormActions = {
    setUserInteractedWithFlexFieldCvv: (interacted: boolean) =>
        flexActions.setUserInteractedWithFlexFieldCvv(interacted),
    setUserInteractedWithFlexField: (interacted: boolean) => flexActions.setUserInteractedWithFlexField(interacted),
    submitForm: (values: BuyFormData) => buyFormActions.submitForm(values),
    submitFormUcs: (values: BuyFormData) => buyFormActions.submitFormUcs(values),
    existingUserSubmitForm: (values: BuyFormData) => buyFormActions.existingUserSubmitForm(values),
    existingUserSubmitFormUCS: (values: BuyFormData) => buyFormActions.existingUserSubmitFormUcs(values),
    validateAddress: (values: BuyFormData, setSubmitButtonDisabled: (value: boolean) => void) =>
        locationActions.validateAddress(values, setSubmitButtonDisabled),
    calculatePrice: (values: BuyFormData) => buyCheckoutActions.calculatePrice(values),
    setFatalError: (isFatalError: boolean, message?: FatalErrorMessageType) =>
        fatalErrorActions.setFatalError(isFatalError, message),
    setCheckoutFormInlineError: (value: string) => commonCheckoutActions.setCheckoutFormInlineError(value),
    removeCheckoutFormInlineError: (value: string) => commonCheckoutActions.removeCheckoutFormInlineError(value),
    userLogin: (pathname: string) => buyUserActions.userLogin('trial checkout view', pathname),
    setEmailClear: (clear: boolean) => buyFormActions.setClearEmail(clear),
    setChallengeCompleteJwt: (jwt: string) => flexActions.setChallengeCompleteJwt(jwt),
    track: (trackingInfo: TrackingState) =>
        buyTrackingActions.track(trackingInfo, BUY_TRACKING_COMPONENTS.CHECKOUT_PAGE),
    setCardinalSetupComplete: () => flexActions.setCardinalSetupComplete(),
    submitAbandonCart: (email: string, form: BuyAbandonedCartFormData) =>
        buyUserActions.submitAbandonedCart({
            success: false,
            isTrial: false,
            program: 'cart',
            email,
            form,
        }),
}

export type BuyCheckoutFormActionsType = typeof mapBuyCheckoutFormActions
